import axios from 'axios';
import {
  User,
  Puzzle,
  PuzzleAttempt,
  PuzzleLeaderboard,
  AllTimeEntry,
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authAPI = {
  register: async (email: string, password: string) => {
    const response = await api.post('/auth/register', { email, password });
    return response.data;
  },
  
  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  
  getCurrentUser: async (): Promise<User> => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  updateDisplayName: async (displayName: string): Promise<User> => {
    const response = await api.patch('/auth/me', { display_name: displayName });
    return response.data;
  },
};

export const puzzleAPI = {
  createPuzzle: async (puzzle: Omit<Puzzle, 'id' | 'creator_id' | 'is_daily' | 'daily_date' | 'created_at' | 'is_published'>) => {
    const response = await api.post('/puzzles', puzzle);
    return response.data;
  },
  
  getPuzzles: async (): Promise<Puzzle[]> => {
    const response = await api.get('/puzzles');
    return response.data;
  },
  
  getPuzzle: async (id: number): Promise<Puzzle> => {
    const response = await api.get(`/puzzles/${id}`);
    return response.data;
  },
  
  getDailyPuzzle: async (): Promise<Puzzle | null> => {
    const response = await api.get('/puzzles/daily/today');
    return response.data;
  },

  getDailyPuzzleArchive: async (skip: number = 0, limit: number = 30): Promise<Puzzle[]> => {
    const response = await api.get('/puzzles/daily/archive', { params: { skip, limit } });
    return response.data;
  },

  fetchDailyPuzzleNow: async () => {
    const response = await api.post('/puzzles/daily/fetch-now');
    return response.data;
  },

  ensureTodayPuzzle: async (): Promise<Puzzle | null> => {
    const response = await api.get('/puzzles/daily/ensure-today');
    return response.data;
  },

  setDailyPuzzle: async (puzzleId: number) => {
    const response = await api.post(`/puzzles/${puzzleId}/set-daily`);
    return response.data;
  },
};

export const attemptAPI = {
  createAttempt: async (puzzleId: number, guestId?: string, gridWidth: number = 5, gridHeight: number = 5): Promise<PuzzleAttempt> => {
    const response = await api.post('/attempts', {
      puzzle_id: puzzleId,
      current_grid: Array(gridHeight).fill(null).map(() => Array(gridWidth).fill('')),
      guest_id: guestId,
    });
    return response.data;
  },
  
  updateAttempt: async (attemptId: number, data: Partial<PuzzleAttempt>): Promise<PuzzleAttempt> => {
    const response = await api.put(`/attempts/${attemptId}`, data);
    return response.data;
  },
  
  getUserAttempt: async (puzzleId: number, guestId?: string): Promise<PuzzleAttempt | null> => {
    const params = guestId ? { guest_id: guestId } : {};
    const response = await api.get(`/attempts/puzzle/${puzzleId}`, { params });
    return response.data;
  },
};

export const statsAPI = {
  getUserStats: async () => {
    const response = await api.get('/stats/user');
    return response.data;
  },
};

export const leaderboardAPI = {
  getPuzzleLeaderboard: async (
    puzzleId: number,
    guestId?: string
  ): Promise<PuzzleLeaderboard> => {
    const response = await api.get(`/leaderboard/puzzle/${puzzleId}`, {
      params: guestId ? { guest_id: guestId } : {},
    });
    return response.data;
  },

  getAllTimeLeaderboard: async (guestId?: string): Promise<AllTimeEntry[]> => {
    const response = await api.get('/leaderboard/all-time', {
      params: guestId ? { guest_id: guestId } : {},
    });
    return response.data;
  },
};

export default api;